import React from "react";
import { WithFieldValue, DocumentData } from "firebase/firestore";
import { Alert, Text } from "@chakra-ui/react";

import { PRODUCTION_STEPS_AUX } from "@constants";
import { useCustomToast, useStoreDebugTags } from "@hooks";

import {
  JsonForm,
  JsonFormControl,
  makeCommentsControl,
  makeSelectControl,
} from "../JsonForm";
import { ButtonModal } from "../ModalWrappers";
import { useAddAuxStep } from "./useAddAuxStep";

type AddDebugTagModalProps = {
  itemId: string;
};

function makeDebugTagControl(tags: string[]) {
  return [
    makeSelectControl(
      "Debug Tag",
      "debugTag",
      tags,
      "Please select an appropriate tag.",
    ),
    makeCommentsControl(),
  ] as JsonFormControl[];
}

export function AddDebugTagModal({ itemId }: AddDebugTagModalProps) {
  const tags = useStoreDebugTags();
  const tagFormControl = React.useMemo(() => makeDebugTagControl(tags), [tags]);
  const addDebugTagToast = useCustomToast(() => (
    <Alert status="success">Debug Tag Added!</Alert>
  ));
  const [error, setError] = React.useState("");
  const addStep = useAddAuxStep(
    itemId,
    PRODUCTION_STEPS_AUX.STEP_AUX_DEBUG_TAG,
    addDebugTagToast,
    setError,
  );
  const doSubmit = React.useCallback(
    async function <T extends WithFieldValue<DocumentData>>(data: T) {
      const comments = data.comments ? (data.comments as string) : "";
      const debugTag = data.debugTag ? (data.debugTag as string) : "";
      if (debugTag) addStep(comments, { debugTag });
      else setError("Select a tag");
    },
    [addStep],
  );

  return (
    <ButtonModal label="Add Debug Tag" title="Add Debug Tag" size="sm">
      <JsonForm fields={tagFormControl} onSubmit={doSubmit} clearAfterSubmit />
      {error ? <Text>{error}</Text> : undefined}
    </ButtonModal>
  );
}
